import AnchorLink from "components/AnchorLink";
import CheckboxForm from "components/CheckboxForm";
import Error from "components/Error";
import Button from "components/buttons/Button";
import InputForm from "components/input/InputForm";
import AuthenticationLayout from "layout/AuthenticationLayout";
import { useLogin } from "pages/authentication/login/useLogin";
import { useTranslation } from "react-i18next";
import { ROUTES } from "routes/routes.const";

function LoginPage() {
  const { t } = useTranslation();
  const {
    phase,
    challenge,
    isLoginPending,
    isVerifyPending,
    login,
    verify,
    register,
    registerCode,
    errors,
    codeErrors,
    serverError,
    backToCredentials,
    showRememberDevice,
  } = useLogin();

  const isBusy = phase === "credentials" ? isLoginPending : isVerifyPending;

  return (
    <AuthenticationLayout title={t("login.title")}>
      {phase === "credentials" ? (
        <form className="space-y-6" onSubmit={login} noValidate={true}>
          <InputForm
            id="email-input"
            type="email"
            label={t("login.email.label")}
            autoComplete="username"
            autoFocus={true}
            {...register("email")}
            error={errors.email?.message}
          />
          <div className="space-y-1">
            <InputForm
              id="password-input"
              type="password"
              label={t("login.password.label")}
              autoComplete="current-password"
              hideOnDataDog={true}
              {...register("password")}
              error={errors.password?.message}
            />
          </div>
          <div className="flex items-center justify-between">
            <CheckboxForm
              id="remember-me"
              label={t("login.remember-me.label")}
              {...register("rememberMe", { required: false })}
            />
            <AnchorLink
              to={ROUTES.FORGOT_PASSWORD}
              label={t("login.forgot-password")}
              className="text-sm font-medium"
              dataActionName="open-forgot-password"
              dataTestId="link-forgot-password"
            />
          </div>
          {!isBusy && <Error error={serverError} />}
          <Button
            type="submit"
            id="login-submit-button"
            className="w-full"
            label={t("login.sign-in")}
            disabled={isBusy}
          />
          <div>
            <div className="flex items-baseline space-x-2 text-sm">
              <span className="text-color-secondary font-medium">{t("login.not-registered")}</span>
              <AnchorLink
                to={ROUTES.REGISTER}
                label={t("login.register")}
                className="font-medium"
                dataActionName="open-register"
                dataTestId="link-register"
              />
            </div>
          </div>
        </form>
      ) : (
        <form className="space-y-6" onSubmit={verify} noValidate={true}>
          <p className="text-color-secondary text-sm">
            {challenge
              ? t("login.two-factor.sent-hint", {
                  maskedEmail: challenge.maskedEmail,
                })
              : null}
          </p>
          <InputForm
            id="two-factor-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            label={t("login.two-factor.code-label")}
            autoFocus={true}
            hideOnDataDog={true}
            {...registerCode("code")}
            error={codeErrors.code?.message}
          />
          {showRememberDevice && (
            <CheckboxForm
              id="remember-device"
              label={t("login.two-factor.remember-device-label")}
              {...registerCode("rememberDevice", { required: false })}
            />
          )}
          {!isBusy && <Error error={serverError} />}
          <Button
            type="submit"
            id="two-factor-verify-button"
            className="w-full"
            label={t("login.two-factor.verify")}
            disabled={isBusy}
          />
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              className="text-color-secondary text-sm font-medium underline"
              onClick={backToCredentials}
              disabled={isBusy}
              data-testid="two-factor-back"
            >
              {t("login.two-factor.back")}
            </button>
          </div>
        </form>
      )}
    </AuthenticationLayout>
  );
}

export default LoginPage;
